import { Avatar, Box, Stack } from '@mui/material';
import { FeedbackDone, Feedback as FeedbackIcon } from 'Images';
import { useEffect, useState } from 'react';
import { Note } from './Note';
import {
  HELPFULNESS,
  NEW_PAGE_THOUGHTS,
  REASONS,
  THOUGHTS,
} from 'Enum/Feedback';
import { UploadFileArea } from './UploadFileArea';
import { useIsDesktop } from 'Hooks';
import { Reason } from './Reason';
import {
  ISitePagesDTO,
  getPageInfo,
  postFeedback,
} from 'API/Feedback/feedback';
import { Success } from './Success';
import { Title } from './Title';
import { Helpfulness } from './Helpfulness';
import { Thoughts } from './Thoughts';
import { File } from './File';
import { LinkToRequest } from './LinkToRequest';
import { Send } from './Send';
import { getFormData } from './Utils/utils';
import { ScreenCapture } from 'Components/Feedback/ScreenCapture';
import { UploadNativeRecording } from './UploadNativeRecording';
import { Info } from './ScreenCapture/Info';
import { detectDeviceInfo } from 'Utils/device';

const defaultErrors = {
  isFileError: false,
  isTypeError: false,
  isNoteError: false,
  isReasonError: false,
};

export const Feedback = () => {
  const [thoughtsValue, setThoughtsValue] = useState<
    THOUGHTS | NEW_PAGE_THOUGHTS | ''
  >('');
  const [helpfulnessValue, setHelpfulnessValue] = useState<HELPFULNESS | ''>(
    ''
  );
  const [reasonValue, setReasonValue] = useState<REASONS | ''>('');
  const [noteValue, setNoteValue] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [recordingFile, setRecordingFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<{
    isFileError: boolean;
    isTypeError: boolean;
    isNoteError: boolean;
    isReasonError: boolean;
  }>(defaultErrors);
  const [isDone, setIsDone] = useState(false);
  const [currentPage, setCurrentPage] = useState<ISitePagesDTO | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);

  const isDesktop = useIsDesktop();

  const onNoteChange = (value: string) => {
    setNoteValue(value);
  };

  useEffect(() => {
    if (thoughtsValue !== THOUGHTS.improvement) setReasonValue('');
  }, [thoughtsValue]);

  const initPageInfo = async () => {
    const newPageInfo = await getPageInfo(
      window.location.pathname.replace(/^\//, '')
    );

    if (newPageInfo) setCurrentPage(newPageInfo);
    setHelpfulnessValue('');
    setThoughtsValue('');
    setReasonValue('');
    setNoteValue('');
    setFile(null);
    setErrors(defaultErrors);
    setIsDone(false);
  };

  useEffect(() => {
    initPageInfo();
  }, [window.location.pathname]);

  const onSubmit = () => {
    const isNoteError = noteValue.trim() === '';
    const isReasonError =
      (thoughtsValue === THOUGHTS.improvement ||
        thoughtsValue === NEW_PAGE_THOUGHTS.other) &&
      reasonValue === '';

    setErrors((prevErrors) => ({
      ...prevErrors,
      isNoteError: isNoteError,
      isReasonError: isReasonError,
    }));

    const { deviceId, browserId, deviceDetails } = detectDeviceInfo();

    if (
      !isNoteError &&
      !isReasonError &&
      !Object.values(errors).includes(true)
    ) {
      postFeedback(
        getFormData({
          currentPage,
          helpfulnessValue,
          thoughtsValue,
          reasonValue,
          noteValue,
          file,
          recordedBlob,
          deviceId,
          browserId,
          deviceDetails,
        }),
        currentPage?.isNew
      );
      setIsDone(true);
    }
  };

  return (
    <Stack
      sx={{
        ...(isDesktop
          ? {
              p: '48px 88px 88px',
              bgcolor: '#F6F8FE',
              flexDirection: 'row',
              alignItems: 'start',
              width: '100%',
            }
          : {
              bgcolor: 'primary.shade_50',
              width: '100vw',
              ml: '-24px',
              p: '24px',
              gap: '16px',
            }),
      }}
    >
      {isDone ? (
        <Success isTechnicalProblem={thoughtsValue === THOUGHTS.problem} />
      ) : (
        <Stack
          sx={{
            gap: '32px',
            maxWidth: isDesktop ? '600px' : '100%',
          }}
        >
          <Title isNew={!!currentPage?.isNew} />
          {currentPage?.isNew && (
            <Helpfulness
              helpfulnessValue={helpfulnessValue}
              setHelpfulnessValue={(value) => {
                setHelpfulnessValue(value);
                if (value === HELPFULNESS.Helped)
                  setThoughtsValue(THOUGHTS.like);
              }}
            />
          )}
          {(!currentPage?.isNew || helpfulnessValue !== '') &&
            helpfulnessValue !== HELPFULNESS.Helped && (
              <Thoughts
                thoughtsValue={thoughtsValue}
                setThoughtsValue={setThoughtsValue}
                options={
                  currentPage?.isNew
                    ? Object.values(NEW_PAGE_THOUGHTS)
                    : Object.values(THOUGHTS)
                }
              />
            )}

          {(thoughtsValue === THOUGHTS.improvement ||
            thoughtsValue === NEW_PAGE_THOUGHTS.other) && (
            <Reason
              reasonValue={reasonValue}
              setReasonValue={(reason) => {
                setReasonValue(reason);
                if (reason !== '')
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    isReasonError: false,
                  }));
              }}
              isReasonError={errors.isReasonError}
            />
          )}
          {thoughtsValue !== '' && (
            <Box sx={{ width: isDesktop ? '600px' : '100%' }}>
              <Note
                value={noteValue}
                onChange={onNoteChange}
                thoughtsValue={thoughtsValue}
                error={errors.isNoteError}
                setError={(isError) =>
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    isNoteError: isError,
                  }))
                }
              />
            </Box>
          )}
          {(thoughtsValue === THOUGHTS.improvement ||
            thoughtsValue === THOUGHTS.problem ||
            thoughtsValue === NEW_PAGE_THOUGHTS.other) && (
            <>
              {!!file ? (
                <File file={file} setFile={setFile} />
              ) : (
                <UploadFileArea
                  isTypeError={errors.isTypeError}
                  setIsTypeError={(isError) =>
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      isTypeError: isError,
                    }))
                  }
                  setFile={setFile}
                />
              )}
              {!isDesktop ? (
                recordingFile ? (
                  <File file={recordingFile} setFile={setRecordedBlob} />
                ) : (
                  <Stack>
                    <UploadNativeRecording
                      isTypeError={errors.isTypeError}
                      setIsTypeError={(isError) =>
                        setErrors((prevErrors) => ({
                          ...prevErrors,
                          isTypeError: isError,
                        }))
                      }
                      setRecordedBlob={setRecordedBlob}
                      setFile={setRecordingFile}
                    />
                    <Info showError={errors.isTypeError} />
                  </Stack>
                )
              ) : (
                <ScreenCapture
                  setRecordedBlob={setRecordedBlob}
                  recordedBlob={recordedBlob}
                />
              )}
            </>
          )}
          <LinkToRequest />
          {thoughtsValue !== '' && <Send onClickSend={onSubmit} />}
        </Stack>
      )}
      <Avatar
        alt=''
        src={isDone ? FeedbackDone : FeedbackIcon}
        sx={{
          ...(isDesktop
            ? { width: '220px', height: '138.5px', order: 1 }
            : {
                width: isDone ? '102px' : '145px',
                height: isDone ? '93px' : '103px',
                m: '0 auto',
                order: -1,
              }),
        }}
      />
    </Stack>
  );
};
